import {
   ApiEmissionFactor,
   ApiMaterialGrade,
   ApiMaterialType,
   ApiSubtype,
   CategoryListItem,
   EmissionFactorFormState,
   EmissionFactorsApiResponse,
   getApiMaterialGrades,
   getApiMaterialTypes,
   getApiSubtypes,
   getEmissionFactorCategories,
   MaterialCategory,
   MaterialGrade,
   MaterialSubtype,
   MaterialType,
} from '../types/emission-factor.api';
import { useCallback, useEffect, useRef, useState } from 'react';

import APIService from '../../../services/api.service';
import { formatProgressPercent, getProgressRatio, getProgressStatusColor } from '../../../shared/utils/progress.utils';

interface CachedResponses {
   byCategory: Record<string, CategoryListItem[]>;
   byMaterialType: Record<string, CategoryListItem[]>;
   bySubtype: Record<string, CategoryListItem[]>;
   byGrade: Record<string, EmissionFactorsApiResponse>;
}

interface HierarchyMatch {
   category: MaterialCategory;
   materialType: MaterialType;
   subtype: MaterialSubtype;
   grade?: MaterialGrade;
}

interface WalkFilter {
   categoryId?: string | null;
   materialTypeId?: string | null;
   subtypeId?: string | null;
   gradeId?: string | null;
}

const formatDropdownDisplay = (id: string, name?: string): string =>
   name && name.trim() !== '' ? `${id} - ${name}` : id;

const toProgressProps = (progressCurrent?: number, progressTotal?: number) => {
   const ratio = getProgressRatio(progressCurrent, progressTotal);
   if (ratio === null) {
      return {
         progressText: undefined,
         progressStatusColor: undefined,
      };
   }

   return {
      progressText: formatProgressPercent(ratio),
      progressStatusColor: getProgressStatusColor(ratio),
   };
};

type ProgressListItem = Pick<CategoryListItem, 'id' | 'name' | 'progressCurrent' | 'progressTotal'>;

const toHierarchyItem = ({ id, name, progressCurrent, progressTotal }: ProgressListItem) => ({
   id,
   displayName: formatDropdownDisplay(id, name),
   ...toProgressProps(progressCurrent, progressTotal),
});

const toGrade = (mg: ApiMaterialGrade): MaterialGrade => ({
   id: mg.id,
   displayName: formatDropdownDisplay(mg.id, mg.name),
});

const toSubtype = (st: ApiSubtype): MaterialSubtype => ({
   id: st.id,
   displayName: formatDropdownDisplay(st.id, st.name),
   grades: getApiMaterialGrades(st).map(toGrade),
});

const toMaterialType = (mt: ApiMaterialType): MaterialType => ({
   id: mt.id,
   displayName: formatDropdownDisplay(mt.id, mt.name),
   subtypes: getApiSubtypes(mt).map(toSubtype),
});

const toCategory = (ef: ApiEmissionFactor): MaterialCategory => ({
   id: ef.id,
   displayName: formatDropdownDisplay(ef.id, ef.name),
   materialTypes: getApiMaterialTypes(ef).map(toMaterialType),
});

const walkResponse = (response: EmissionFactorsApiResponse, filter?: WalkFilter): HierarchyMatch[] => {
   const matches: HierarchyMatch[] = [];
   for (const ef of getEmissionFactorCategories(response)) {
      if (filter?.categoryId && ef.id !== filter.categoryId) continue;
      const category = toCategory(ef);
      for (const mt of getApiMaterialTypes(ef)) {
         if (filter?.materialTypeId && mt.id !== filter.materialTypeId) continue;
         const materialType = toMaterialType(mt);
         for (const st of getApiSubtypes(mt)) {
            if (filter?.subtypeId && st.id !== filter.subtypeId) continue;
            const subtype = toSubtype(st);
            const materialGrades = getApiMaterialGrades(st);
            if (materialGrades.length === 0 && !filter?.gradeId) {
               matches.push({ category, materialType, subtype });
            } else {
               for (const mg of materialGrades) {
                  if (filter?.gradeId && mg.id !== filter.gradeId) continue;
                  matches.push({ category, materialType, subtype, grade: toGrade(mg) });
               }
            }
         }
      }
   }
   return matches;
};

const uniqueById = <T extends { id: string }>(items: T[]): T[] => {
   const seen = new Set<string>();
   return items.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
   });
};

const mergeCategories = (prev: MaterialCategory[], toAdd: MaterialCategory[]): MaterialCategory[] => {
   const newOnes = toAdd.filter((nc) => !prev.some((c) => c.id === nc.id));
   if (newOnes.length === 0) return prev;
   return [...prev, ...newOnes].sort((a, b) => a.id.localeCompare(b.id));
};

export interface HierarchyNavigation {
   formState: EmissionFactorFormState;
   categories: MaterialCategory[];
   materialTypes: MaterialType[];
   subtypes: MaterialSubtype[];
   grades: MaterialGrade[];
   loadingCategories: boolean;
   loadingMaterialTypes: boolean;
   loadingSubtypes: boolean;
   loadingGrades: boolean;
   loadingDirectInput: boolean;
   loadingInitialGrade: boolean;
   error: string | null;
   clearError: () => void;
   setSelectedCategory: (id: string | null) => void;
   setSelectedMaterialType: (id: string | null) => void;
   setSelectedSubtype: (id: string | null) => void;
   setSelectedGrade: (id: string | null) => void;
   handleDirectCategoryInput: (id: string) => void;
   handleDirectMaterialTypeInput: (id: string) => void;
   handleDirectSubtypeInput: (id: string) => void;
   handleDirectGradeInput: (id: string) => void;
   clearSelection: () => void;
   setFormState: React.Dispatch<React.SetStateAction<EmissionFactorFormState>>;
   cachedResponsesRef: React.MutableRefObject<CachedResponses>;
}

function useCachedHierarchyLevel<TResponse, TItems>(
   enabled: boolean,
   cacheKey: string | null,
   cacheBucket: Record<string, TResponse> | undefined,
   fetcher: () => Promise<TResponse>,
   extract: (resp: TResponse) => TItems,
   setItems: (items: TItems) => void,
   emptyValue: TItems,
   errorMessage: string,
   setError: (msg: string) => void,
   setLoading: (loading: boolean) => void
) {
   useEffect(() => {
      if (!enabled || !cacheKey || !cacheBucket) {
         setItems(emptyValue);
         return;
      }
      let isCurrent = true;
      const load = async () => {
         setLoading(true);
         try {
            let resp = cacheBucket[cacheKey];
            if (!resp) {
               resp = await fetcher();
               cacheBucket[cacheKey] = resp;
            }
            if (isCurrent) setItems(extract(resp));
         } catch (err) {
            if (isCurrent) {
               setError(errorMessage);
               console.error(err);
            }
         } finally {
            if (isCurrent) setLoading(false);
         }
      };
      load();
      return () => {
         isCurrent = false;
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [enabled, cacheKey]);
}

export function useHierarchyNavigation(materialGradeId?: string, errorAutoDismissMs = 6000): HierarchyNavigation {
   const [formState, setFormState] = useState<EmissionFactorFormState>({
      selectedCategory: null,
      selectedMaterialType: null,
      selectedSubtype: null,
      selectedGrade: null,
      hasChanges: false,
   });

   const [categories, setCategories] = useState<MaterialCategory[]>([]);
   const [materialTypes, setMaterialTypes] = useState<MaterialType[]>([]);
   const [subtypes, setSubtypes] = useState<MaterialSubtype[]>([]);
   const [grades, setGrades] = useState<MaterialGrade[]>([]);

   const [loadingInitialGrade, setLoadingInitialGrade] = useState(false);
   const [loadingCategories, setLoadingCategories] = useState(false);
   const [loadingMaterialTypes, setLoadingMaterialTypes] = useState(false);
   const [loadingSubtypes, setLoadingSubtypes] = useState(false);
   const [loadingGrades, setLoadingGrades] = useState(false);
   const [loadingDirectInput, setLoadingDirectInput] = useState(false);
   const [error, setError] = useState<string | null>(null);

   const cachedResponsesRef = useRef<CachedResponses>({
      byCategory: {},
      byMaterialType: {},
      bySubtype: {},
      byGrade: {},
   });

   const operationIdRef = useRef(0);

   const clearError = useCallback(() => setError(null), []);

   useEffect(() => {
      if (!error) return;
      const timer = setTimeout(() => setError(null), errorAutoDismissMs);
      return () => clearTimeout(timer);
   }, [error, errorAutoDismissMs]);

   useEffect(() => {
      let isCurrent = true;
      const load = async () => {
         setLoadingCategories(true);
         setError(null);
         try {
            const categoryItems = await APIService.getEmissionFactorCategories();
            if (!isCurrent) return;
            setCategories(
               categoryItems.map((item) => ({
                  ...toHierarchyItem(item),
                  materialTypes: [],
               }))
            );
         } catch (err) {
            if (isCurrent) {
               setError('Failed to load emission factor categories');
               console.error('Error loading categories:', err);
            }
         } finally {
            if (isCurrent) setLoadingCategories(false);
         }
      };
      load();
      return () => {
         isCurrent = false;
      };
   }, []);

   const { selectedCategory, selectedMaterialType, selectedSubtype } = formState;

   useCachedHierarchyLevel(
      !!selectedCategory,
      selectedCategory,
      cachedResponsesRef.current.byCategory,
      () => APIService.getEmissionFactorTypes(selectedCategory!),
      (items) =>
         items.map((item) => ({
            ...toHierarchyItem(item),
            subtypes: [],
         })),
      setMaterialTypes,
      [],
      'Failed to load material types',
      setError,
      setLoadingMaterialTypes
   );

   useCachedHierarchyLevel(
      !!(selectedCategory && selectedMaterialType),
      selectedCategory && selectedMaterialType ? `${selectedCategory}-${selectedMaterialType}` : null,
      cachedResponsesRef.current.byMaterialType,
      () => APIService.getEmissionFactorSubtypes(selectedCategory!, selectedMaterialType!),
      (items) =>
         items.map((item) => ({
            ...toHierarchyItem(item),
            grades: [],
         })),
      setSubtypes,
      [],
      'Failed to load subtypes',
      setError,
      setLoadingSubtypes
   );

   useCachedHierarchyLevel(
      !!(selectedCategory && selectedMaterialType && selectedSubtype),
      selectedCategory && selectedMaterialType && selectedSubtype
         ? `${selectedCategory}-${selectedMaterialType}-${selectedSubtype}`
         : null,
      cachedResponsesRef.current.bySubtype,
      () => APIService.getEmissionFactorGradeIds(selectedCategory!, selectedMaterialType!, selectedSubtype!),
      (items) => items.map(toHierarchyItem),
      setGrades,
      [],
      'Failed to load grades',
      setError,
      setLoadingGrades
   );

   useEffect(() => {
      if (!materialGradeId || categories.length === 0) return;
      let isCurrent = true;
      const find = async () => {
         setLoadingInitialGrade(true);
         try {
            const resp = await APIService.getEmissionFactors({ gradeId: materialGradeId });
            cachedResponsesRef.current.byGrade[materialGradeId] = resp;
            if (!isCurrent) return;
            const match = walkResponse(resp, { gradeId: materialGradeId })[0];
            if (match?.grade) {
               setFormState({
                  selectedCategory: match.category.id,
                  selectedMaterialType: match.materialType.id,
                  selectedSubtype: match.subtype.id,
                  selectedGrade: match.grade.id,
                  hasChanges: false,
               });
            }
         } catch (err) {
            if (isCurrent) console.error('Error finding grade:', err);
         } finally {
            if (isCurrent) setLoadingInitialGrade(false);
         }
      };
      find();
      return () => {
         isCurrent = false;
      };
   }, [materialGradeId, categories.length]);

   const setSelectedCategory = useCallback((categoryId: string | null) => {
      setFormState((prev) => {
         if (prev.selectedCategory === categoryId) return prev;
         setMaterialTypes([]);
         setSubtypes([]);
         setGrades([]);
         return { ...prev, selectedCategory: categoryId, selectedMaterialType: null, selectedSubtype: null, selectedGrade: null };
      });
   }, []);

   const setSelectedMaterialType = useCallback((materialTypeId: string | null) => {
      setFormState((prev) => {
         if (prev.selectedMaterialType === materialTypeId) return prev;
         setSubtypes([]);
         setGrades([]);
         return { ...prev, selectedMaterialType: materialTypeId, selectedSubtype: null, selectedGrade: null };
      });
   }, []);

   const setSelectedSubtype = useCallback((subtypeId: string | null) => {
      setFormState((prev) => {
         if (prev.selectedSubtype === subtypeId) return prev;
         setGrades([]);
         return { ...prev, selectedSubtype: subtypeId, selectedGrade: null };
      });
   }, []);

   const setSelectedGrade = useCallback((gradeId: string | null) => {
      setFormState((prev) => (prev.selectedGrade === gradeId ? prev : { ...prev, selectedGrade: gradeId }));
   }, []);

   const runDirectInput = useCallback(
      async <T>(action: () => Promise<T | null>, onSuccess: (result: T) => void, onError: () => void, notFoundMsg: string) => {
         operationIdRef.current += 1;
         const opId = operationIdRef.current;
         setLoadingDirectInput(true);
         setError(null);
         try {
            const result = await action();
            if (operationIdRef.current !== opId) return;
            if (result === null) {
               setError(notFoundMsg);
               onError();
               return;
            }
            onSuccess(result);
         } catch (err) {
            if (operationIdRef.current !== opId) return;
            console.error(err);
            setError(notFoundMsg);
            onError();
         } finally {
            if (operationIdRef.current === opId) setLoadingDirectInput(false);
         }
      },
      []
   );

   type HierarchyLevel = 'category' | 'materialType' | 'subtype' | 'grade';

   const resetFromLevel = useCallback((level: HierarchyLevel) => {
      if (level === 'category') {
         setFormState((prev) => ({
            ...prev,
            selectedCategory: null,
            selectedMaterialType: null,
            selectedSubtype: null,
            selectedGrade: null,
         }));
         setMaterialTypes([]);
         setSubtypes([]);
         setGrades([]);
         return;
      }
      if (level === 'materialType') {
         setFormState((prev) => ({
            ...prev,
            selectedMaterialType: null,
            selectedSubtype: null,
            selectedGrade: null,
         }));
         setSubtypes([]);
         setGrades([]);
         return;
      }
      if (level === 'subtype') {
         setFormState((prev) => ({ ...prev, selectedSubtype: null, selectedGrade: null }));
         setGrades([]);
         return;
      }
      setFormState((prev) => ({ ...prev, selectedGrade: null }));
   }, []);

   const applyMatchSets = useCallback(
      (
         matches: HierarchyMatch[],
         options: { includeGrades?: boolean; resetHasChanges?: boolean } = {}
      ) => {
         const { includeGrades = false, resetHasChanges = false } = options;
         const cats = uniqueById(matches.map((m) => m.category));
         const mts = uniqueById(matches.map((m) => m.materialType));
         const sts = uniqueById(matches.map((m) => m.subtype));
         const gs = includeGrades ? uniqueById(matches.flatMap((m) => (m.grade ? [m.grade] : []))) : [];

         setCategories((prev) => mergeCategories(prev, cats));
         setMaterialTypes(mts);
         setSubtypes(sts);
         setGrades(includeGrades ? gs : []);

         const nextSelections = {
            selectedCategory: cats.length === 1 ? cats[0].id : null,
            selectedMaterialType: mts.length === 1 ? mts[0].id : null,
            selectedSubtype: sts.length === 1 ? sts[0].id : null,
            selectedGrade: includeGrades && gs.length === 1 ? gs[0].id : null,
         };

         if (resetHasChanges) {
            setFormState({ ...nextSelections, hasChanges: false });
         } else {
            setFormState((prev) => ({ ...prev, ...nextSelections }));
         }
      },
      []
   );

   const handleDirectCategoryInput = useCallback(
      (categoryId: string) => {
         if (categories.some((c) => c.id === categoryId)) {
            setSelectedCategory(categoryId);
            return;
         }
         runDirectInput(
            async () => {
               const resp = await APIService.getEmissionFactors({ categoryId });
               return getEmissionFactorCategories(resp).length === 0 ? null : resp;
            },
            (resp) => {
               const newCat = walkResponse(resp, { categoryId })[0]?.category;
               if (newCat) {
                  setCategories((prev) => mergeCategories(prev, [newCat]));
               }
               setSelectedCategory(categoryId);
            },
            () => resetFromLevel('category'),
            `Category "${categoryId}" not found`
         );
      },
      [categories, setSelectedCategory, runDirectInput, resetFromLevel]
   );

   const handleDirectMaterialTypeInput = useCallback(
      (materialTypeId: string) => {
         if (formState.selectedCategory && materialTypes.some((m) => m.id === materialTypeId)) {
            setSelectedMaterialType(materialTypeId);
            return;
         }
         runDirectInput(
            async () => {
               const resp = await APIService.getEmissionFactors({ materialTypeId });
               const matches = walkResponse(resp, { materialTypeId });
               return matches.length === 0 ? null : matches;
            },
            (matches) => applyMatchSets(matches),
            () => resetFromLevel('materialType'),
            `Material type "${materialTypeId}" not found`
         );
      },
      [formState.selectedCategory, materialTypes, setSelectedMaterialType, runDirectInput, applyMatchSets, resetFromLevel]
   );

   const handleDirectSubtypeInput = useCallback(
      (subtypeId: string) => {
         if (formState.selectedMaterialType && subtypes.some((s) => s.id === subtypeId)) {
            setSelectedSubtype(subtypeId);
            return;
         }
         runDirectInput(
            async () => {
               const resp = await APIService.getEmissionFactors({ subtypeId });
               const matches = walkResponse(resp, { subtypeId });
               return matches.length === 0 ? null : matches;
            },
            (matches) => applyMatchSets(matches),
            () => resetFromLevel('subtype'),
            `Subtype "${subtypeId}" not found`
         );
      },
      [formState.selectedMaterialType, subtypes, setSelectedSubtype, runDirectInput, applyMatchSets, resetFromLevel]
   );

   const handleDirectGradeInput = useCallback(
      (gradeId: string) => {
         if (formState.selectedSubtype && grades.some((g) => g.id === gradeId)) {
            setSelectedGrade(gradeId);
            return;
         }
         runDirectInput(
            async () => {
               const resp = await APIService.getEmissionFactors({ gradeId });
               if (getEmissionFactorCategories(resp).length === 0) return null;
               cachedResponsesRef.current.byGrade[gradeId] = resp;
               const matches = walkResponse(resp, { gradeId });
               return matches.length === 0 ? null : matches;
            },
            (matches) => applyMatchSets(matches, { includeGrades: true, resetHasChanges: true }),
            () => resetFromLevel('grade'),
            `Grade "${gradeId}" not found`
         );
      },
      [formState.selectedSubtype, grades, setSelectedGrade, runDirectInput, applyMatchSets, resetFromLevel]
   );

   const clearSelection = useCallback(() => {
      setFormState({ selectedCategory: null, selectedMaterialType: null, selectedSubtype: null, selectedGrade: null, hasChanges: false });
      setMaterialTypes([]);
      setSubtypes([]);
      setGrades([]);
   }, []);

   return {
      formState,
      setFormState,
      categories,
      materialTypes,
      subtypes,
      grades,
      loadingCategories,
      loadingMaterialTypes,
      loadingSubtypes,
      loadingGrades,
      loadingDirectInput,
      loadingInitialGrade,
      error,
      clearError,
      setSelectedCategory,
      setSelectedMaterialType,
      setSelectedSubtype,
      setSelectedGrade,
      handleDirectCategoryInput,
      handleDirectMaterialTypeInput,
      handleDirectSubtypeInput,
      handleDirectGradeInput,
      clearSelection,
      cachedResponsesRef,
   };
}